import React, { useState } from "react";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import { ApolloError } from "apollo-client";
import { Modal, Box, Flex, Text, TouchableOpacity, ChevronLeft, Input, Button } from "@atoms";
import { CircleAddIcon, ImageListItem } from "@molecules";
import { useToggle, useStoreAppValue } from "@hooks";
import { Bugsnag } from "@services";
import { CreateSubGroupFragment } from "./__generated__/CreateSubGroupFragment";
import {
  CreateSubGroupMutation,
  CreateSubGroupMutationVariables,
} from "./__generated__/CreateSubGroupMutation";

// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps {
  groups: CreateSubGroupFragment[];
}

// == Constants ============================================================

CreateSubGroup.fragment = gql`
  fragment CreateSubGroupFragment on Group {
    id
    name
  }
`;

const CREATE_SUB_GROUP_MUTATION = gql`
  mutation CreateSubGroupMutation($name: String!) {
    CampaignGroupCreate(name: $name) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on Campaign {
        id
        name
        groups {
          id
          name
          fundraisers {
            id
            name
            amountRaised {
              dollarsAmount
              formatted
            }
          }
        }
        fundraisers {
          id
          name
          group {
            id
            name
          }
        }
      }
    }
  }
`;

// == Component ============================================================

export default function CreateSubGroup({ groups }: IProps) {
  const [isCreateSubGroupModalOpen, setIsCreateSubGroupModalOpen] = useToggle(false);
  const [pendingGroupName, setPendingGroupName] = useState("");
  const isPendingGroupNameUsed = groups.map((group) => group.name).includes(pendingGroupName);

  const onError = (errors?: ApolloError) => {
    if (errors) Bugsnag.notify(errors);
    Materialize?.toast?.(
      `There was an ERROR creating the subgroup. Please try again.`,
      4000,
      "rounded toast-danger"
    );
    setIsCreateSubGroupModalOpen(false);
    setPendingGroupName("");
  };

  const [createCreateSubGroupMutation, { loading }] = useMutation<
    CreateSubGroupMutation,
    CreateSubGroupMutationVariables
  >(CREATE_SUB_GROUP_MUTATION, {
    onError,
    onCompleted: ({ CampaignGroupCreate }) => {
      if (CampaignGroupCreate.__typename === "MutationError") {
        onError(CampaignGroupCreate?.errors);
        return;
      }
      Materialize?.toast?.(
        `Group ${pendingGroupName} has been created`,
        4000,
        "rounded toast-success"
      );
      setIsCreateSubGroupModalOpen(false);
      setPendingGroupName("");
    },
  });

  const primary = useStoreAppValue()?.theme?.color?.primary ?? "#27B7BA";

  const onTouchCreateSubgroup = () => {
    return setIsCreateSubGroupModalOpen(true);
  };

  const onChange = (e) => {
    setPendingGroupName(e.target.value);
  };

  const onClickCreateSubgroup = () => {
    return isPendingGroupNameUsed
      ? null
      : createCreateSubGroupMutation({ variables: { name: pendingGroupName } });
  };

  const onCancel = () => {
    setIsCreateSubGroupModalOpen(false);
    setPendingGroupName("");
  };

  return (
    <>
      <ImageListItem
        leftComponent={<CircleAddIcon />}
        padding={[1, 2]}
        text="Create Subgroup"
        onTouch={onTouchCreateSubgroup}
      />
      <Modal
        isOpen={isCreateSubGroupModalOpen}
        onDismiss={() => setIsCreateSubGroupModalOpen(false)}
      >
        <Flex alignItems="center" flexDirection="column" justifyContent="center" padding={3}>
          <Flex alignSelf="flex-start">
            <TouchableOpacity onTouch={() => setIsCreateSubGroupModalOpen(false)}>
              <ChevronLeft color={primary} fontSize={[2, 3, 4]} />
            </TouchableOpacity>
            <Box paddingLeft={[2, 3]}>
              <Text fontWeight="bold">Create Subgroup</Text>
            </Box>
          </Flex>
          <Box padding={3} textAlign="center" width={0.8}>
            <Text fontSize={[0, 1]}>
              Encourage some friendly competition amongst your team and reach your goals faster by
              forming subgroups (guys vs gals, seniors vs juniors, etc).
            </Text>
          </Box>
          <Flex flexDirection="row" justifyContent="center" py={2} width={0.6}>
            <Input
              height={40}
              minWidth={200}
              placeholder="Subgroup Name"
              value={pendingGroupName}
              width={0.6}
              onChange={(e) => onChange(e)}
            />
          </Flex>
          <Flex alignItems="center" justifyContent="center" py={2}>
            <Button
              height={50}
              minWidth={200}
              variant={isPendingGroupNameUsed ? "disabled" : "primary"}
              onClick={onClickCreateSubgroup}
            >
              Create Subgroup
            </Button>
          </Flex>
          <Box py={2} textAlign="center">
            <Button height={50} minWidth={200} variant="clear" onClick={onCancel}>
              <Text color="primary" fontSize={[1, 2]}>
                Cancel
              </Text>
            </Button>
          </Box>
        </Flex>
      </Modal>
    </>
  );
}

// == Styles ===============================================================
